/* global GMAPS_API_KEY, google */
/*
 * PRD-037 — Autocompletado de direccion del portal del partner (app SEPARADA).
 * Reusa el mismo loader de Google Places que el panel (useJsApiLoader + GMAPS_API_KEY,
 * libreria 'places') para llenar la direccion y capturar lat/lng al elegir una sugerencia.
 * Degrada a input de texto libre si la API no carga (sin coords, no bloquea el alta).
 */
import { useRef, type CSSProperties } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import type { Libraries } from '@interfaces/Map';
import { C } from './ui';

const LIBRARIES: Libraries = ['places'];
const FONT = 'Poppins, sans-serif';

export interface AddressValue {
  direccion: string;
  lat: number | null;
  lng: number | null;
}

interface Props {
  value: AddressValue;
  onChange: (v: AddressValue) => void;
  label?: string;
}

const inputStyle: CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  fontSize: 14,
  fontFamily: FONT,
  color: C.primary,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 6,
  boxSizing: 'border-box',
  outline: 'none',
};

export const AddressAutocomplete = ({ value, onChange, label = 'Direccion del negocio (opcional)' }: Props) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GMAPS_API_KEY, libraries: LIBRARIES });
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  const handlePlaceChanged = () => {
    const place = acRef.current?.getPlace();
    if (!place) return;
    const loc = place.geometry?.location;
    onChange({
      direccion: place.formatted_address || value.direccion,
      lat: loc ? loc.lat() : null,
      lng: loc ? loc.lng() : null,
    });
  };

  // Texto libre: cualquier edicion manual invalida las coords previas (solo una sugecion las captura).
  const inputEl = (
    <input
      style={inputStyle}
      placeholder="Escribe y elige la direccion"
      value={value.direccion}
      onChange={(e) => onChange({ direccion: e.target.value, lat: null, lng: null })}
    />
  );

  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.primary, marginBottom: 6 }}>
        {label}
      </span>
      {isLoaded ? (
        <Autocomplete
          onLoad={(ac) => {
            acRef.current = ac;
          }}
          onPlaceChanged={handlePlaceChanged}
          options={{ componentRestrictions: { country: 'mx' }, fields: ['formatted_address', 'geometry'] }}
        >
          {inputEl}
        </Autocomplete>
      ) : (
        inputEl
      )}
      {value.lat !== null && value.lng !== null && (
        <span style={{ display: 'block', fontSize: 11, color: C.success, marginTop: 6 }}>Ubicacion capturada ✓</span>
      )}
    </label>
  );
};
